import { createContext, useReducer, useEffect } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../firebase/config";
import { signOutIfUnverified } from "@/utils/emailVerificationGate";

export const AuthContext = createContext();

export const authReducer = (state, action) => {
  switch (action.type) {
    case "LOGIN":
      return {
        ...state,
        user: action.payload?.user || action.payload,
        customClaims: action.payload?.customClaims || state.customClaims || {},
      };
    case "LOGOUT":
      return { ...state, user: null, customClaims: {} };
    case "AUTH_IS_READY":
      return {
        ...state,
        user: action.payload.user,
        customClaims: action.payload.customClaims || {},
        authIsReady: true,
      };
    case "SET_CUSTOM_CLAIMS":
      return { ...state, customClaims: action.payload || {} };
    default:
      return state;
  }
};

export const getAuthToken = async (forceRefresh = false) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not found");
  }
  try {
    return await user.getIdToken(forceRefresh);
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const getAuthClaims = async (forceRefresh = false) => {
  const user = auth.currentUser;
  if (!user) {
    return {};
  }
  try {
    const tokenResult = await user.getIdTokenResult(forceRefresh);
    return tokenResult.claims || {};
  } catch (error) {
    console.error(error);
    return {};
  }
};

export function AuthContextProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, {
    getAuthToken,
    getAuthClaims,
    user: null,
    customClaims: {},
    authIsReady: false,
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      let customClaims = {};
      if (user) {
        if (await signOutIfUnverified(user)) {
          dispatch({
            type: "AUTH_IS_READY",
            payload: { user: null, customClaims: {} },
          });
          unsub();
          return;
        }
        const url = new URL(window.location.href);
        if (url.searchParams.has("t")) {
          url.searchParams.delete("t");
          window.history.replaceState(
            {},
            "",
            `${url.pathname}${url.search}${url.hash}`
          );
        }
        try {
          const tokenResult = await user.getIdTokenResult(true);
          customClaims = tokenResult.claims || {};
        } catch (error) {
          const code = String(error?.code || "");
          const sessionEnded = [
            "auth/user-disabled",
            "auth/user-token-expired",
            "auth/id-token-revoked",
            "auth/invalid-user-token",
          ].includes(code);
          if (sessionEnded) {
            await signOut(auth).catch(() => {});
            dispatch({
              type: "AUTH_IS_READY",
              payload: { user: null, customClaims: {} },
            });
            return;
          }
          console.error("Error loading custom claims", error);
        }
      }

      dispatch({
        type: "AUTH_IS_READY",
        payload: { user, customClaims },
      });
      unsub();
    });
  }, []);

  // const foo = useMemo(() => ({ ...state, dispatch }), []);
  return (
    <AuthContext.Provider value={{ ...state, dispatch }}>
      {children}
    </AuthContext.Provider>
  );
}
